import { analysisSectionItems, defaultAnalysisSection } from "../../pages/AnalysisPage.jsx";
import { defaultHelpSection } from "../../pages/HelpPage.jsx";
import { defaultSettingsSection, settingsSectionItems } from "../../pages/SettingsPage.jsx";
import { fallbackPageKey, isValidPageKey } from "./appRouteKeys.js";

export const pageSectionConfig = {
  analysis: {
    defaultSection: defaultAnalysisSection,
    sessionKey: "wallet-watch:analysis-section",
    validSections: new Set(analysisSectionItems.map((item) => item.value))
  },
  dev: {
    defaultSection: "index",
    sessionKey: "wallet-watch:dev-section",
    validSections: new Set(["index", "projection-formula"])
  },
  settings: {
    defaultSection: defaultSettingsSection,
    sessionKey: "wallet-watch:settings-section",
    validSections: new Set(settingsSectionItems.map((item) => item.value))
  },
  help: {
    defaultSection: defaultHelpSection,
    sessionKey: "wallet-watch:help-section",
    validSections: new Set(["shortcuts", "tips"])
  }
};

function getSessionValue(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function setSessionValue(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore session storage failures
  }
}

function getRememberedSection(pageKey) {
  const config = pageSectionConfig[pageKey];
  if (!config) {
    return "";
  }

  const rememberedValue = getSessionValue(config.sessionKey);
  return config.validSections.has(rememberedValue)
    ? rememberedValue
    : config.defaultSection;
}

function getRememberedSections() {
  return Object.fromEntries(
    Object.keys(pageSectionConfig).map((pageKey) => [pageKey, getRememberedSection(pageKey)])
  );
}

export function rememberSection(pageKey, section) {
  const config = pageSectionConfig[pageKey];
  if (!config || !config.validSections.has(section)) {
    return;
  }

  setSessionValue(config.sessionKey, section);
}

export function routeFromHashPath(hashPath = "", rememberedSections = {}) {
  const normalizedHashPath = String(hashPath ?? "").replace(/^#\//, "").trim();
  const [rawPageKey = "", rawSection = ""] = normalizedHashPath.split("/");
  const pageKey = isValidPageKey(rawPageKey) ? rawPageKey : fallbackPageKey;
  const sections = Object.fromEntries(
    Object.entries(pageSectionConfig).map(([key, config]) => {
      const rememberedValue = rememberedSections[key];
      const section = pageKey === key
        ? (config.validSections.has(rawSection)
            ? rawSection
            : (key === "dev"
                ? config.defaultSection
                : (config.validSections.has(rememberedValue) ? rememberedValue : config.defaultSection)))
        : (config.validSections.has(rememberedValue) ? rememberedValue : config.defaultSection);

      return [key, section];
    })
  );

  return {
    isBareRoot: normalizedHashPath === "",
    pageKey,
    sections
  };
}

export function routeFromHash() {
  const hashPath = window.location.hash.replace(/^#\//, "").trim();

  return routeFromHashPath(hashPath, getRememberedSections());
}

export function buildHashForRoute(pageKey, sections) {
  const config = pageSectionConfig[pageKey];
  if (config) {
    const section = sections[pageKey] ?? config.defaultSection;
    if (pageKey === "dev" && section === "index") {
      return "#/dev";
    }

    return `#/${pageKey}/${section}`;
  }

  return `#/${pageKey}`;
}
